// bill_subsystem_v1 + product_commission_v1 + optional_price_v1 + commission_tiered_v1: tiered commission
import React, { useState, useEffect } from 'react';
import API from '../api';

function fmtCommission(p) {
  var rate = Number(p.commission_rate || 0);
  if (rate <= 0) return '-';
  if (p.commission_type === 'fixed') return '฿' + rate.toLocaleString('th-TH');
  return rate.toFixed(2).replace(/\.?0+$/, '') + '%';
}

function fmtMoney(n) {
  return Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 0 });
}

const EMPTY_FORM = { name: '', description: '', category: '', unit_price: '', commission_rate: '', commission_type: 'percent', commission_min_charge: '', commission_min_base: '', commission_max_base: '', sort_order: 0, is_active: true };

export default function BillProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function load() {
    setLoading(true);
    API.get('/api/products')
      .then(function (r) { setProducts(r.data || []); })
      .catch(function () {})
      .finally(function () { setLoading(false); });
  }

  useEffect(function () { load(); }, []);

  function resetForm() {
    setForm(EMPTY_FORM);
    setEditing(null);
    setShowForm(false);
    setError('');
  }

  function openCreate() {
    resetForm();
    setShowForm(true);
  }

  function openEdit(p) {
    setEditing(p);
    setForm({
      name: p.name || '',
      description: p.description || '',
      category: p.category || '',
      unit_price: String(p.unit_price || 0),
      commission_rate: p.commission_rate ? String(p.commission_rate) : '',
      commission_type: p.commission_type || 'percent',
      commission_min_charge: Number(p.commission_min_charge || 0) > 0 ? String(p.commission_min_charge) : '',
      commission_min_base:   Number(p.commission_min_base   || 0) > 0 ? String(p.commission_min_base)   : '',
      commission_max_base:   Number(p.commission_max_base   || 0) > 0 ? String(p.commission_max_base)   : '',
      sort_order: p.sort_order || 0,
      is_active: p.is_active !== false,
    });
    setShowForm(true);
    setError('');
  }

  async function handleSave() {
    setError('');
    if (!form.name.trim()) { setError('กรอกชื่อสินค้า'); return; }
    /* optional_price_v1: empty = not fixed (treated as 0) */
    var price = form.unit_price === '' ? 0 : Number(form.unit_price);
    if (!isFinite(price) || price < 0) { setError('ราคาต้องเป็นตัวเลข ≥ 0 หรือเว้นว่าง'); return; }
    var rate = Number(form.commission_rate || 0);
    if (form.commission_rate !== '' && (!isFinite(rate) || rate < 0)) { setError('คอมมิชชั่นต้องเป็นตัวเลข ≥ 0'); return; }

    setSaving(true);
    /* commission_tiered_v1: tiered thresholds */
    var minCharge = Number(form.commission_min_charge || 0);
    var minBase   = Number(form.commission_min_base   || 0);
    var maxBase   = Number(form.commission_max_base   || 0);
    if (minCharge < 0) { setError('ขั้นต่ำต้อง ≥ 0'); return; }
    if (minBase < 0) { setError('ฐานต่ำสุดต้อง ≥ 0'); return; }
    if (maxBase < 0) { setError('ฐานสูงสุดต้อง ≥ 0'); return; }
    if (maxBase > 0 && minBase > 0 && maxBase < minBase) { setError('ฐานสูงสุดต้อง ≥ ฐานต่ำสุด'); return; }

    var payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      category: form.category.trim() || null,
      unit_price: price,
      commission_rate: rate,
      commission_type: form.commission_type,
      commission_min_charge: minCharge,
      commission_min_base: minBase,
      commission_max_base: maxBase,
      sort_order: Number(form.sort_order) || 0,
      is_active: !!form.is_active,
    };

    try {
      if (editing) {
        await API.put('/api/products/' + editing.id, payload);
      } else {
        await API.post('/api/products', payload);
      }
      resetForm();
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.error) || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(p) {
    try {
      await API.patch('/api/products/' + p.id + '/toggle');
      load();
    } catch (err) {
      alert((err.response && err.response.data && err.response.data.error) || 'อัปเดตไม่สำเร็จ');
    }
  }

  async function handleDelete(p) {
    if (!window.confirm('ลบ ' + p.name + '?')) return;
    try {
      await API.delete('/api/products/' + p.id);
      load();
    } catch (err) {
      alert((err.response && err.response.data && err.response.data.error) || 'ลบไม่สำเร็จ');
    }
  }

  var filtered = products;
  if (search.trim()) {
    var q = search.trim().toLowerCase();
    filtered = products.filter(function (p) {
      return ((p.name || '').toLowerCase().indexOf(q) >= 0) ||
             ((p.category || '').toLowerCase().indexOf(q) >= 0);
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>สินค้า / บริการ</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input value={search} onChange={function (e) { setSearch(e.target.value); }} placeholder="ค้นหา..." style={{ width: 180, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, fontFamily: 'inherit' }} />
          <button onClick={openCreate} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>+ เพิ่ม</button>
        </div>
      </div>

      {loading ? (
        <div style={{ color: '#94a3b8' }}>กำลังโหลด...</div>
      ) : filtered.length === 0 ? (
        <div style={{ background: 'white', padding: 40, borderRadius: 12, textAlign: 'center', color: '#94a3b8', border: '1px solid #e2e8f0' }}>{products.length === 0 ? 'ยังไม่มีสินค้า — กด "+ เพิ่ม"' : 'ไม่พบสินค้าที่ค้นหา'}</div>
      ) : (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                <th style={{ padding: 12 }}>ชื่อ</th>
                <th style={{ padding: 12 }}>หมวด</th>
                <th style={{ padding: 12, textAlign: 'right' }}>ราคา</th>
                <th style={{ padding: 12, textAlign: 'right' }}>คอมมิชชั่น</th>
                <th style={{ padding: 12, textAlign: 'center' }}>สถานะ</th>
                <th style={{ padding: 12, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(function (p) {
                return (
                  <tr key={p.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: 12 }}>
                      <div>{p.name}</div>
                      {p.description ? <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{p.description}</div> : null}
                    </td>
                    <td style={{ padding: 12, color: '#64748b', fontSize: 13 }}>{p.category || '-'}</td>
                    <td style={{ padding: 12, textAlign: 'right' }}>{Number(p.unit_price || 0) > 0 ? fmtMoney(p.unit_price) : <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 12 }}>ตามใจ</span>}</td>
                    <td style={{ padding: 12, textAlign: 'right', color: Number(p.commission_rate||0) > 0 ? '#10b981' : '#94a3b8', fontWeight: Number(p.commission_rate||0) > 0 ? 600 : 400 }}>{fmtCommission(p)}</td>
                    <td style={{ padding: 12, textAlign: 'center' }}>{p.is_active ? <span style={{ color: '#10b981' }}>✓</span> : <span style={{ color: '#94a3b8' }}>✗</span>}</td>
                    <td style={{ padding: 12, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={function () { openEdit(p); }} style={{ background: 'none', border: '1px solid #e2e8f0', padding: '4px 10px', borderRadius: 6, fontSize: 12, marginRight: 4, cursor: 'pointer' }}>แก้ไข</button>
                      <button onClick={function () { handleToggle(p); }} style={{ background: 'none', border: '1px solid #e2e8f0', padding: '4px 10px', borderRadius: 6, fontSize: 12, marginRight: 4, cursor: 'pointer' }}>{p.is_active ? 'ปิด' : 'เปิด'}</button>
                      <button onClick={function () { handleDelete(p); }} style={{ background: 'none', border: '1px solid #fecaca', color: '#dc2626', padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>ลบ</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div onClick={function () { if (!saving) resetForm(); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 100 }}>
          <div onClick={function (e) { e.stopPropagation(); }} style={{ background: 'white', borderRadius: 14, padding: 24, maxWidth: 540, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 16px' }}>{editing ? 'แก้ไขสินค้า' : 'เพิ่มสินค้า'}</h3>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: '#475569' }}>ชื่อสินค้า / บริการ <span style={{ color: '#dc2626' }}>*</span></label>
              <input value={form.name} onChange={function (e) { setForm(Object.assign({}, form, { name: e.target.value })); }} autoFocus style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: '#475569' }}>คำอธิบาย</label>
              <textarea value={form.description} onChange={function (e) { setForm(Object.assign({}, form, { description: e.target.value })); }} rows={2} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 13, color: '#475569' }}>หมวด</label>
                <input value={form.category} onChange={function (e) { setForm(Object.assign({}, form, { category: e.target.value })); }} placeholder="เช่น รายเดือน" style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 13, color: '#475569' }}>ราคา (฿) <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>— เว้นว่างถ้าไม่ fix ราคา</span></label>
                <input type="number" min="0" step="0.01" value={form.unit_price} onChange={function (e) { setForm(Object.assign({}, form, { unit_price: e.target.value })); }} placeholder="เว้นว่าง = กรอกตอนทำบิล" style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ background: '#f8fafc', padding: 12, borderRadius: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: '#475569', marginBottom: 8, fontWeight: 600 }}>💰 คอมมิชชั่น</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b' }}>อัตรา</label>
                  <input type="number" min="0" step="0.01" value={form.commission_rate} onChange={function (e) { setForm(Object.assign({}, form, { commission_rate: e.target.value })); }} placeholder="0" style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b' }}>หน่วย</label>
                  <select value={form.commission_type} onChange={function (e) { setForm(Object.assign({}, form, { commission_type: e.target.value })); }} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, fontFamily: 'inherit' }}>
                    <option value="percent">% (เปอร์เซ็นต์)</option>
                    <option value="fixed">฿ (บาท ต่อชิ้น)</option>
                  </select>
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>ตัวอย่าง: 5% ของยอด หรือ ฿20 ต่อชิ้น</div>

              {/* commission_tiered_v1: tiered thresholds */}
              <div style={{ borderTop: '1px dashed #cbd5e1', marginTop: 12, paddingTop: 10 }}>
                <div style={{ fontSize: 12, color: '#475569', fontWeight: 600, marginBottom: 8 }}>📊 ขั้นบันได (optional — เว้นว่าง = ไม่ใช้)</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#64748b' }}>ขั้นต่ำเก็บ (฿)</label>
                    <input type="number" min="0" step="0.01" value={form.commission_min_charge} onChange={function (e) { setForm(Object.assign({}, form, { commission_min_charge: e.target.value })); }} placeholder="30000" style={{ width: '100%', padding: 7, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, marginTop: 3, boxSizing: 'border-box' }} />
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>ถ้าคำนวณได้น้อยกว่านี้ ใช้ค่านี้</div>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#64748b' }}>ยอดเริ่มคิด (฿)</label>
                    <input type="number" min="0" step="0.01" value={form.commission_min_base} onChange={function (e) { setForm(Object.assign({}, form, { commission_min_base: e.target.value })); }} placeholder="500000" style={{ width: '100%', padding: 7, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, marginTop: 3, boxSizing: 'border-box' }} />
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>ยอดต่ำกว่านี้ ไม่คิด</div>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#64748b' }}>ยอดเลิกใช้ขั้นต่ำ (฿)</label>
                    <input type="number" min="0" step="0.01" value={form.commission_max_base} onChange={function (e) { setForm(Object.assign({}, form, { commission_max_base: e.target.value })); }} placeholder="1000000" style={{ width: '100%', padding: 7, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, marginTop: 3, boxSizing: 'border-box' }} />
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>ยอดเกินนี้ คิดตามจริง</div>
                  </div>
                </div>
                <div style={{ fontSize: 10, color: '#0ea5e9', marginTop: 6, fontStyle: 'italic' }}>💡 ตัวอย่าง 3% / ขั้นต่ำ 30,000 / เริ่ม 500,000 / เลิก 1,000,000 → ยอด 800k = 30k, ยอด 2M = 60k, ยอด 400k = 0</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 13, color: '#475569' }}>ลำดับ</label>
                <input type="number" value={form.sort_order} onChange={function (e) { setForm(Object.assign({}, form, { sort_order: e.target.value })); }} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 13, color: '#475569' }}>สถานะ</label>
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={form.is_active} onChange={function (e) { setForm(Object.assign({}, form, { is_active: e.target.checked })); }} />
                    เปิดใช้งาน
                  </label>
                </div>
              </div>
            </div>

            {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>⚠️ {error}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={resetForm} disabled={saving} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', fontSize: 14 }}>ยกเลิก</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: saving ? '#94a3b8' : '#10b981', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>{saving ? 'กำลังบันทึก...' : 'บันทึก'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
